import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Creates the initial user document structure with simplified data
 * Only essential fields are populated during registration
 */
export const createInitialUserDocument = (userId, userData) => {
  const now = new Date().toISOString();

  return {
    userId: userId,
    personalInformation: {
      fullName: userData.name || '',
      mobileNumber: userData.phoneNumber || userData.mobile || '',
      whatsappNumber: userData.whatsappNumber || userData.phoneNumber || userData.mobile || '',
      email: userData.email || '',
      address: userData.address || ''
    },
    accountSecurity: {
      accountRole: userData.role || 'User',
      detailedPurpose: userData.purpose || '',
      referredBy: userData.referredBy || '',
      referralInput: userData.referralInput || ''
    },
    herbalifeDetails: {
      herbalifeId: userData.herbalifeId || '',
      preferredStatus: userData.preferredStatus || '',
      herbalifeLastName: userData.herbalifeLastName || '',
      joinYear: userData.joinYear || '',
      herbalifeEmail: userData.herbalifeEmail || '',
      herbalifePassword: userData.herbalifePassword || '',
      sponsorName: userData.sponsorName || '',
      sponsorLastName: userData.sponsorLastName || '',
      sponsorId: userData.sponsorId || '',
      totalLifetimeVP: 0
    },
    inventory: {
      remainingShakes: 0,
      lastTopUpDate: now
    },
    familyDetails: {
      birthday: userData.familyDetails?.birthday || '',
      maritalStatus: userData.familyDetails?.maritalStatus || '',
      spouseName: userData.familyDetails?.spouseName || '',
      marriageDate: userData.familyDetails?.marriageDate || '',
      children: userData.familyDetails?.children || []
    },
    currentHealthMetrics: {
      lastWeight: parseFloat(userData.weight) || 0,
      lastBMI: parseFloat(userData.bmi) || 0,
      lastBodyFat: parseFloat(userData.bodyFat) || 0,
      lastUpdated: now
    },
    metadata: {
      createdAt: now,
      updatedAt: now,
      isOnboardingComplete: userData.isOnboardingComplete !== undefined ? userData.isOnboardingComplete : false,
      isVerified: userData.isVerified !== undefined ? userData.isVerified : false
    }
  };
};

/**
 * Creates a new user document in Firestore
 * @param {string} userId - The Firebase Auth user ID
 * @param {object} userData - Object containing user data
 * @returns {Promise<void>}
 */
export const createUserInFirestore = async (userId, userData) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDocument = createInitialUserDocument(userId, userData);

    await setDoc(userDocRef, userDocument);
    console.log('User document created successfully in Firestore');
  } catch (error) {
    console.error('Error creating user document in Firestore:', error);
    throw error;
  }
};

/**
 * Gets a user document from Firestore
 * @param {string} userId - The Firebase Auth user ID
 * @returns {Promise<object|null>}
 */
export const getUserFromFirestore = async (userId) => {
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      console.log('No user document found');
      return null;
    }
  } catch (error) {
    console.error('Error getting user document from Firestore:', error);
    throw error;
  }
};

/**
 * Updates user document in Firestore
 * @param {string} userId - The Firebase Auth user ID
 * @param {object} updates - Object containing the fields to update
 * @returns {Promise<void>}
 *
 * @example
 * // Update personal information
 * await updateUserInFirestore(userId, {
 *   'personalInformation.fullName': 'John Doe',
 *   'personalInformation.email': 'john@example.com',
 * });
 *
 * @example
 * // Update health metrics
 * await updateUserInFirestore(userId, {
 *   'currentHealthMetrics.lastWeight': 75,
 *   'currentHealthMetrics.lastBMI': 23.5,
 *   'currentHealthMetrics.lastUpdated': new Date().toISOString()
 * });
 *
 * @example
 * // Update inventory
 * await updateUserInFirestore(userId, {
 *   'inventory.remainingShakes': 10,
 *   'inventory.lastTopUpDate': new Date().toISOString()
 * });
 */
export const updateUserInFirestore = async (userId, updates) => {
  try {
    const userDocRef = doc(db, 'users', userId);

    // Always update the updatedAt timestamp
    const updateData = {
      ...updates,
      'metadata.updatedAt': new Date().toISOString(),
    };

    await updateDoc(userDocRef, updateData);
    console.log('User document updated successfully in Firestore');
  } catch (error) {
    console.error('Error updating user document in Firestore:', error);
    throw error;
  }
};
